// AdminLayout.jsx
import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Settings as SettingsIcon, LogOut, Globe, ChevronLeft, ChevronRight, MessageSquareText } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../utils/axiosInstance';
import socket from '../utils/socket'; // ⬅️ add this if you want live quote updates

export default function AdminLayout() {
  const navigate = useNavigate();



  // ── (optional) sound + toast for new quotes
  const notificationSoundRef = useRef(null);
  const [toast, setToast] = useState(null);

  /* orders (already global) */
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const fetchOrders = async () => {
    if (ordersLoading) return;
    try {
      setOrdersLoading(true);
      const { data } = await axiosInstance.get('/api/orders');
      setOrders(data);
    } finally {
      setOrdersLoading(false);
    }
  };

  /* order-status (already global) */
  const [isOrderingEnabled, setIsOrderingEnabled] = useState(true);
  const [summerTimingEnabled, setSummerTimingEnabled] = useState(false);
  const [orderStatusLoading, setOrderStatusLoading] = useState(true);
  const orderStatusFetchedRef = useRef(false);
  const fetchOrderStatus = async () => {
    if (orderStatusFetchedRef.current) return;
    orderStatusFetchedRef.current = true;
    try {
      setOrderStatusLoading(true);
      const { data } = await axiosInstance.get('/api/settings/order-status');
      setIsOrderingEnabled(Boolean(data?.isOrderingEnabled));
      setSummerTimingEnabled(Boolean(data?.summerTimingEnabled));
    } finally {
      setOrderStatusLoading(false);
    }
  };
  const updateOrderStatus = async (newValue) => {
    const prev = isOrderingEnabled;
    setIsOrderingEnabled(newValue);
    try {
      await axiosInstance.post('/api/settings/order-status', { isOrderingEnabled: newValue });
    } catch {
      setIsOrderingEnabled(prev);
    }
  };
  const updateSummerTiming = async (newValue) => {
    const prev = summerTimingEnabled;
    setSummerTimingEnabled(newValue);
    try {
      await axiosInstance.post('/api/settings/summer-timing', { summerTimingEnabled: newValue });
    } catch {
      setSummerTimingEnabled(prev);
    }
  };

  /* NEW: quotes (GLOBAL) */
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const quotesFetchedRef = useRef(false);

  // Fetch all quotes once; allow force refresh when needed
  const fetchQuotes = async (force = false) => {
    if (quotesFetchedRef.current && !force) return;
    quotesFetchedRef.current = true;
    try {
      setQuotesLoading(true);
      const { data } = await axiosInstance.get('/api/quote-requests');
      setQuotes(data?.data || []);
    } finally {
      setQuotesLoading(false);
    }
  };

  // 🔔 Live new-quote updates (server emits { quote: <doc> })
  useEffect(() => {
    // init sound once
    notificationSoundRef.current = new Audio('/notification.wav');

    const onNewQuote = ({ quote }) => {
      if (!quote?._id) return;
      setQuotes((prev) => {
        // de-dupe or replace if it already exists
        const exists = prev.some((q) => q._id === quote._id);
        const next = exists
          ? prev.map((q) => (q._id === quote._id ? quote : q))
          : [quote, ...prev];
        // keep list sorted newest-first
        next.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return next;
      });
      // optional UX
      notificationSoundRef.current?.play().catch(() => { });
      setToast({ msg: `New quote from ${quote.firstName} ${quote.lastName}` });
      setTimeout(() => setToast(null), 3500);
    };

    socket.on('quote:new', onNewQuote);

    const onQuoteViewed = ({ quoteRequestId, quoteId, viewedAt }) => {
      if (quoteRequestId && viewedAt) {
        setQuotes((prev) =>
          prev.map((q) => {
            if (q._id !== quoteRequestId) return q;
            const prevViewed = q.latestViewedAt ? new Date(q.latestViewedAt) : null;
            const nextViewed = new Date(viewedAt);
            const latestViewedAt =
              prevViewed && prevViewed > nextViewed ? prevViewed : nextViewed;
            return { ...q, latestViewedAt };
          })
        );
      }
      setToast({ msg: `Customer viewed quote ${quoteId || ''}` });
      setTimeout(() => setToast(null), 4000);
    };
    socket.on('quote:viewed', onQuoteViewed);

    return () => {
      socket.off('quote:new', onNewQuote);
      socket.off('quote:viewed', onQuoteViewed);
    };
  }, []);

  /* auth guard */
  const checkTokenValidity = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return navigate('/Taylor/login');
    try {
      const { exp } = jwtDecode(token);
      if (exp < Date.now() / 1000) {
        localStorage.removeItem('adminToken');
        navigate('/Taylor/login');
      }
    } catch {
      localStorage.removeItem('adminToken');
      navigate('/Taylor/login');
    }
  };

  useEffect(() => {
    checkTokenValidity();
    fetchOrders();
    fetchOrderStatus();
    fetchQuotes(); // ⬅️ fetch quotes once globally
    const interval = setInterval(checkTokenValidity, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── sidebar state ─────────────────────────────────────── */
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState(false);
  const sidebarWidth = isCollapsed ? 'w-16' : 'w-48';

  const [fabOpen, setFabOpen] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);


  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/Taylor/login');
  };

  return (
    <div className="flex h-dvh max-h-dvh overflow-hidden">
      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md flex items-center px-4 py-3 z-50">
        <button className="text-candy-primary text-2xl" onClick={() => setMenuOpen(true)}>
          ☰
        </button>
        <span className="ml-4 font-semibold text-candy-primary">Admin Panel</span>
      </div>

      {/* Mobile backdrop */}
      {isMenuOpen && <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setMenuOpen(false)} />}

      {/* Sidebar */}
      <aside className={`z-50 flex flex-col bg-candy-primary text-white h-full transition-all duration-300 ${isMenuOpen ? 'fixed top-0 left-0 w-48' : 'hidden lg:flex'} ${sidebarWidth} lg:relative lg:z-auto`}>
        <div className="flex-1 flex flex-col pt-6">
          <nav className="space-y-2 px-2">
            <NavLink to="/Taylor/dashboard" className={({ isActive }) => `flex items-center gap-3 p-3 rounded-md transition ${isActive ? 'bg-candy-primaryDark font-semibold' : 'hover:bg-candy-primaryDark'}`}>
              <LayoutDashboard size={18} />
              {!isCollapsed && <span>Dashboard</span>}
            </NavLink>
            <NavLink to="/Taylor/all-orders" className={({ isActive }) => `flex items-center gap-3 p-3 rounded-md transition ${isActive ? 'bg-candy-primaryDark font-semibold' : 'hover:bg-candy-primaryDark'}`}>
              <ClipboardList size={18} />
              {!isCollapsed && <span>All Orders</span>}
            </NavLink>
            <NavLink to="/Taylor/quotes" className={({ isActive }) => `flex items-center gap-3 p-3 rounded-md transition ${isActive ? 'bg-candy-primaryDark font-semibold' : 'hover:bg-candy-primaryDark'}`}>
              <MessageSquareText size={18} />
              {!isCollapsed && <span>Quotes</span>}
            </NavLink>
            <NavLink to="/Taylor/manage-site" className={({ isActive }) => `flex items-center gap-3 p-3 rounded-md transition ${isActive ? 'bg-candy-primaryDark font-semibold' : 'hover:bg-candy-primaryDark'}`}>
              <Globe size={18} />
              {!isCollapsed && <span>Manage</span>}
            </NavLink>
          </nav>

          {/* Collapse control (desktop) */}
          <div className="hidden lg:flex flex-1 items-center justify-center">
            <button className="flex items-center justify-center py-2 text-white" onClick={() => setCollapsed(!isCollapsed)}>
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        </div>

        {/* Bottom links */}
        <div className="pb-4 px-2">
          <NavLink to="/Taylor/settings" className={({ isActive }) => `flex items-center gap-3 p-3 rounded-md transition ${isActive ? 'bg-candy-primaryDark font-semibold' : 'hover:bg-candy-primaryDark'}`}>
            <SettingsIcon size={18} />
            {!isCollapsed && <span>Settings</span>}
          </NavLink>
          <button onClick={handleLogout} className="flex items-center gap-3 p-3 w-full text-left rounded-md hover:bg-candy-primaryDark transition">
            <LogOut size={18} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Floating Action Button */}
      <div className="fixed bottom-10 right-0 z-50 transition-all duration-300 flex flex-row items-end space-x-8 mr-8 opacity-60 hover:opacity-100">
        <div
          className={`flex items-center gap-3 transition-all duration-300 ${fabOpen
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-4 pointer-events-none'
            }`}
        >
          {/* Create Payment Link Button (Round FAB) */}
          <button
            onClick={() => setShowPaymentLinkModal(true)}
            className="w-16 h-16 bg-candy-primary text-white rounded-full shadow-xl 
                 flex items-center justify-center text-3xl 
                 hover:bg-candy-primaryDark transition"
          >
            <span className='pb-2'>💳</span>
          </button>
        </div>

        {/* Main FAB Button */}
        <button
          onClick={() => setFabOpen(!fabOpen)}
          className="w-16 h-16 bg-candy-primary rounded-full shadow-2xl 
               flex items-center justify-center text-white text-5xl 
               hover:bg-candy-primaryDark transition"
        >
          <span className='pb-2'>{fabOpen ? '×' : '+'}</span>
        </button>
      </div>


      {/* Payment Link Modal */}
      {showPaymentLinkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">

            <h2 className="text-xl font-semibold text-candy-primary mb-4">
              Create Payment Link
            </h2>

            <PaymentLinkForm onClose={() => setShowPaymentLinkModal(false)} />
          </div>
        </div>
      )}



      {/* Content */}
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain bg-candy-background pt-14 lg:pt-0">
        <Outlet
          context={{
            // orders
            orders, setOrders, fetchOrders, ordersLoading,
            // order-status
            isOrderingEnabled, orderStatusLoading, updateOrderStatus,
            summerTimingEnabled, updateSummerTiming,
            // quotes (GLOBAL)
            quotes, setQuotes, quotesLoading, fetchQuotes,
          }}
        />

        {toast && (
          <div className="fixed right-4 bottom-4 z-50 bg-candy-primary text-white px-4 py-3 rounded-xl shadow-lg">
            {toast.msg}
          </div>
        )}

      </main>
    </div>
  );
}



function PaymentLinkForm({ onClose }) {
  const [amount, setAmount] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copyStatus, setCopyStatus] = useState("idle");


  const createLink = async () => {
    if (!amount) return alert("Amount is required");
    setLoading(true);
    try {
      const { data } = await axiosInstance.post('/api/payments/create-manual-link', {
        amount,
        customerName,
        customerEmail
      });
      setGeneratedLink(data.url);
    } catch (err) {
      alert("Failed to generate link");
    }
    setLoading(false);
  };

  return (
    <div>
      <label className="block mb-2">
        Amount ($)
        <input
          type="number"
          className="w-full mt-1 p-2 border rounded"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>

      <label className="block mb-2">
        Customer Name (optional)
        <input
          type="text"
          className="w-full mt-1 p-2 border rounded"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
        />
      </label>

      <label className="block mb-4">
        Customer Email (optional)
        <input
          type="email"
          className="w-full mt-1 p-2 border rounded"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
        />
      </label>

      {generatedLink ? (
        <div className="bg-green-50 p-4 rounded border border-green-300">
          <p className="font-medium mb-2">Payment Link Generated:</p>

          <button
            onClick={() => {
              navigator.clipboard.writeText(generatedLink);
              setCopyStatus("copied");
              setTimeout(() => setCopyStatus("idle"), 1500);
            }}
            onMouseEnter={() => setCopyStatus("hover")}
            onMouseLeave={() => setCopyStatus("idle")}
            className="text-left w-full text-blue-600 underline break-all hover:text-blue-800 transition relative"
          >
            {generatedLink}

            {/* Tooltip */}
            {copyStatus === "hover" && (
              <span className="absolute -top-6 left-0 bg-black text-white text-xs px-2 py-1 rounded">
                Click to copy
              </span>
            )}

            {/* Copied bubble */}
            {copyStatus === "copied" && (
              <span className="absolute -top-6 left-0 bg-green-600 text-white text-xs px-2 py-1 rounded animate-fade">
                Copied!
              </span>
            )}
          </button>
        </div>
      ) : (
        <button
          onClick={createLink}
          disabled={loading}
          className="w-full bg-candy-primary text-white p-2 rounded hover:bg-candy-primaryDark transition"
        >
          {loading ? "Generating..." : "Create Link"}
        </button>
      )}


      <button
        onClick={onClose}
        className="w-full mt-4 text-gray-600 underline"
      >
        Close
      </button>
    </div>
  );
}
